import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Gavel, Package, ShoppingCart, Users } from "lucide-react";
import AppContext from "../../../context/AppContext";
import StatsCard from "../../../components/admin/adminstatcard/StatCard";
import RecentOrdersTable from "../../../components/admin/adminrecentOrderTable/RecentOrderTable";
import InventoryAlerts from "../../../components/admin/admininventoryAlert/InventoryAlert";
import "../../../assets/css/admin/admindashboard/AdminDashboard.css";

/* Config for each stat card — maps backend stat keys to display info */
const statsConfig = [
  {
    key: "totalOrders",
    title: "Total Orders",
    icon: ShoppingCart,
    link: "/admin/order-list",
  },
  {
    key: "totalProducts",
    title: "Total Products",
    icon: Package,
    link: "/admin/product-list",
  },
  {
    key: "totalCustomers",
    title: "Total Customers",
    icon: Users,
    link: "/admin/customers",
  },
  {
    key: "activeBids",
    title: "Active Bids",
    icon: Gavel,
    link: "/admin/bidding-list",
  },
];

const Dashboard = () => {
  const { baseUrl, authToken } = useContext(AppContext);

  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);

  /* ── Fetch Dashboard Stats ── */
  const fetchStats = () => {
    setStatsLoading(true);

    axios
      .get(`${baseUrl}/admin/dashboard/getStats`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.success) {
          setStats(response.data.stats || {});
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setStatsLoading(false);
      });
  };

  /* ── Fetch Recent Orders ── */
  const fetchRecentOrders = () => {
    setOrdersLoading(true);

    axios
      .get(`${baseUrl}/admin/dashboard/getRecentOrders`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.success) {
          setOrders(response.data.recentOrders || []);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setOrdersLoading(false);
      });
  };

  /* ── Fetch Inventory Stock Alerts ── */
  const fetchStockAlerts = () => {
    setAlertsLoading(true);

    axios
      .get(`${baseUrl}/admin/inventory/viewStockAlerts`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setAlerts(response.data.alerts || []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setAlertsLoading(false);
      });
  };

  useEffect(() => {
    if (authToken) {
      fetchStats();
      fetchRecentOrders();
      fetchStockAlerts();
    }
  }, [authToken]);

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-content">
        {/* Top Section - Stats Cards */}
        <div className="top-section">
          <div className="stats-column">
            {statsConfig.map((cfg) => {
              const stat = stats[cfg.key] || { total: 0, percentChange: 0 };
              const percentChange = stat.percentChange ?? 0;

              return (
                <StatsCard
                  key={cfg.key}
                  title={cfg.title}
                  value={
                    statsLoading
                      ? "..."
                      : (stat.total ?? 0).toLocaleString()
                  }
                  change={`${Math.abs(percentChange)}%`}
                  isPositive={percentChange >= 0}
                  icon={cfg.icon}
                  iconBg="rgba(214, 126, 67, 0.85)"
                  link={cfg.link}
                />
              );
            })}
          </div>
        </div>

        <div className="orders-row">
          <RecentOrdersTable orders={orders} loading={ordersLoading} />
        </div>

        <div className="alerts-row">
          <InventoryAlerts alerts={alerts} loading={alertsLoading} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;